import type { ReactNode } from 'react'
import { ArrowLeft, ArrowRight, Inbox } from 'lucide-react'
import { Link } from '../../../router.js'
import { usePermissions } from '../../../admin/primitives.js'
import type { Lecture } from '../../api.js'
import { fmtDateHeure } from '../../api.js'
import { BandeauFige, BandeauLecture, fmtAge, fmtHeure, LIBELLE_GRAVITE, Pastille, TON_GRAVITE, useVueSonde, type Ton } from '../commun.js'
import {
  estListeDecouvertes, estListeEvenements, LIBELLE_SOURCE, LIEN_A_VALIDER, lienAppareil, texteEvenement, TON_SOURCE,
  type Collecteur, type EvenementSonde, type ListeDecouvertes, type SourceDomaine,
} from './kit-modele.js'
import { useMenuSonde } from '../menu-sonde.js'
import './kit.css'

/**
 * SUPERVISION — BRIQUES D'ÉCRAN DES VUES DE DOMAINE (vidéo, audiovisuel,
 * bâtiment), 21/10/2026. Classes `sv-dom-*` (kit.css), code couleur unique
 * de `commun.tsx`. Chaque bloc dit d'où vient son chiffre et de quand.
 */

/** sonde.details.read : identifiants techniques (clés d'entité, chemins, codes). */
export function usePeutDetails(): boolean {
  const { permissions } = usePermissions()
  return permissions.includes('sonde.details.read')
}

export function usePeut(droit: string): boolean {
  const { permissions } = usePermissions()
  return permissions.includes(droit)
}

/** La donnée à afficher : la lecture du moment, sinon la dernière réussie (alors grisée). */
export function donneeDe<T>(lecture: Lecture<T> | null, precedente: T | null): { d: T | null; perime: boolean; charge: boolean } {
  if (lecture?.etat === 'ok') return { d: lecture.data, perime: false, charge: true }
  return { d: precedente, perime: precedente !== null && lecture !== null, charge: lecture !== null }
}

/** Journal d'événements de Sonde filtré par types (24 h par défaut côté Sonde). */
export function useEvenements(kinds: string[], limit = 20) {
  const chemin = `events?kind=${encodeURIComponent(kinds.join(','))}&limit=${limit}`
  return useVueSonde(chemin, estListeEvenements, 30_000)
}

/**
 * Propositions « À valider » : lues seulement pour qui peut les traiter
 * (sonde.registry.manage). On n'en garde que le genre proposé, jamais une
 * adresse ni une MAC.
 */
export function useADecider(source: string | null): ListeDecouvertes | null {
  // Le relais sert « discovery » au détail technique (sonde.details.read) :
  // sans lui, la lecture serait refusée — on ne la tente pas.
  const gere = usePeut('sonde.registry.manage')
  const details = usePeut('sonde.details.read')
  const peut = gere && details
  const chemin = peut ? `discovery?status=pending&limit=500${source ? `&source=${encodeURIComponent(source)}` : ''}` : null
  const { lecture } = useVueSonde(chemin, estListeDecouvertes, 300_000)
  return lecture?.etat === 'ok' ? lecture.data : null
}

export function EnTeteDomaine(props: {
  retour: { href: string; label: string }
  icone: ReactNode
  titre: string
  sousTitre: string
  genereA: string | null | undefined
  perime: boolean
}) {
  return (
    <header className="sv-dom-tete">
      <Link className="lc-crumb" href={props.retour.href}><ArrowLeft size={14} aria-hidden="true" /> {props.retour.label}</Link>
      <div className="sv-dom-tete__ligne">
        <span className="sv-dom-tete__icone" aria-hidden="true">{props.icone}</span>
        <div className="sv-dom-tete__textes">
          <h1 className="lc-title">{props.titre}</h1>
          <p className="lc-subtitle">{props.sousTitre}</p>
        </div>
        <p className={`sv-dom-tete__lu${props.perime ? ' sv-dom-tete__lu--perime' : ''}`}>
          <span className="sv-dom-tete__point" aria-hidden="true" />
          {props.genereA ? <>Lu sur Sonde à <span className="sv-dom-mono">{fmtHeure(props.genereA)}</span></> : 'Lecture en cours…'}
        </p>
      </div>
    </header>
  )
}

/** Bandeaux d'état d'une lecture : indisponible (gris) et collecteur figé. */
export function Bandeaux({ lecture, genereA, collector }: { lecture: Lecture<unknown> | null; genereA: string | null | undefined; collector: Collecteur | null | undefined }) {
  return (
    <>
      <BandeauLecture lecture={lecture} depuis={genereA ?? null} />
      <BandeauFige collector={collector ?? null} />
    </>
  )
}

export function Panneau(props: { id: string; titre: string; indice?: ReactNode | undefined; droite?: ReactNode | undefined; children: ReactNode; className?: string | undefined }) {
  return (
    <section className={`lc-panel sv-dom-panneau${props.className ? ` ${props.className}` : ''}`} aria-labelledby={props.id}>
      <header className="lc-panel__head sv-dom-panneau__tete">
        <h2 className="lc-section-title" id={props.id}>{props.titre}</h2>
        {props.indice ? <span className="lc-hint sv-dom-panneau__indice">{props.indice}</span> : null}
        {props.droite ? <span className="sv-dom-panneau__droite">{props.droite}</span> : null}
      </header>
      <div className="lc-panel__body sv-dom-panneau__corps">{props.children}</div>
    </section>
  )
}

/** Contenu d'une dernière lecture réussie alors que Sonde ne répond plus : grisé, jamais rouge. */
export function Zone({ perime, children }: { perime: boolean; children: ReactNode }) {
  return <div className={perime ? 'sv-dom-zone sv-dom-zone--perime' : 'sv-dom-zone'}>{children}</div>
}

/** État vide : une phrase qui dit pourquoi, et où agir. Jamais d'illustration. */
export function Vide({ titre, children, versADecider }: { titre: string; children?: ReactNode; versADecider?: boolean }) {
  return (
    <div className="sv-dom-vide">
      <Inbox size={18} aria-hidden="true" />
      <div className="sv-dom-vide__corps">
        <p className="sv-dom-vide__titre">{titre}</p>
        {children ? <div className="sv-dom-vide__texte">{children}</div> : null}
        {versADecider ? (
          <Link className="lc-btn sv-dom-vide__lien" href={LIEN_A_VALIDER}>Ouvrir « À valider » <ArrowRight size={14} aria-hidden="true" /></Link>
        ) : null}
      </div>
    </div>
  )
}

/** Une tuile chiffrée : valeur, ton, d'où elle vient et de quand. */
export function Tuile(props: { libelle: string; valeur: ReactNode; ton?: Ton | 'neutre' | null | undefined; detail?: ReactNode | undefined; source?: string | null | undefined; age?: number | null | undefined; href?: string | undefined }) {
  const corps = (
    <>
      <span className="sv-dom-tuile__libelle">{props.libelle}</span>
      <span className="sv-dom-tuile__valeur">{props.valeur}</span>
      {props.detail ? <span className="sv-dom-tuile__detail">{props.detail}</span> : null}
      <span className="sv-dom-tuile__src">{props.source ?? 'Sonde'} · {fmtAge(props.age)}</span>
    </>
  )
  const cls = `sv-dom-tuile sv-dom-tuile--${props.ton ?? 'neutre'}`
  return props.href ? <Link className={cls} href={props.href}>{corps}</Link> : <div className={cls}>{corps}</div>
}

export function Tuiles({ children }: { children: ReactNode }) {
  return <div className="sv-dom-tuiles">{children}</div>
}

/** Âge et source d'un chiffre, en petit. */
export function Lu({ age, source }: { age: number | null | undefined; source?: string | null }) {
  return <span className="sv-dom-lu">{source ? `${source} · ` : ''}{fmtAge(age)}</span>
}

/**
 * Les sources de la vue : une source indisponible est grise, jamais « hors
 * ligne ». ATTENTION : chez Sonde, `last_success_at` n'est écrit qu'au
 * CHANGEMENT d'état (c'est « en état OK depuis »), pas à chaque relève. On
 * l'affiche donc comme tel ; l'âge des mesures est donné par chaque section.
 * Une source « OK » qui n'a encore rien relevé est grise : « Pas encore lue ».
 */
export function Sources({ sources, details }: { sources: SourceDomaine[]; details: boolean }) {
  if (sources.length === 0) return <p className="lc-hint">Aucune source déclarée pour cette vue.</p>
  return (
    <ul className="sv-dom-sources">
      {sources.map((s) => {
        const okDepuis = s.status === 'OK' ? s.since ?? s.last_success_at ?? null : null
        const jamaisLue = s.status === 'OK' && !okDepuis
        const ton = jamaisLue ? 'gris' : TON_SOURCE[s.status] ?? 'gris'
        const erreur = s.status !== 'OK' && s.last_error_label ? s.last_error_label : null
        return (
          <li key={s.code} className="sv-dom-source">
            <span className="sv-dom-source__nom">{s.label}{details ? <span className="sv-dom-mono sv-dom-source__code"> {s.code}</span> : null}</span>
            <Pastille ton={ton}>{jamaisLue ? 'Pas encore lue' : s.status_label && s.status !== 'OK' ? s.status_label : LIBELLE_SOURCE[s.status] ?? s.status}</Pastille>
            <span className="sv-dom-source__meta">
              {okDepuis ? `Lue sans erreur depuis ${fmtDateHeure(okDepuis)}`
                : jamaisLue ? 'Aucune relève encore'
                  : s.status === 'NOT_CONFIGURED' ? 'Clé d’accès non saisie'
                    : s.since ? `Dans cet état depuis ${fmtDateHeure(s.since)}` : '—'}
              {erreur ? ` · ${erreur}` : ''}
            </span>
          </li>
        )
      })}
    </ul>
  )
}

/** Journal : message de Sonde, conséquence d'abord, avec l'heure et l'appareil. */
export function Journal({ lecture, precedente, vide }: { lecture: Lecture<{ items: EvenementSonde[] }> | null; precedente: { items: EvenementSonde[] } | null; vide: string }) {
  const { d, perime } = donneeDe(lecture, precedente)
  if (!d) return lecture && lecture.etat !== 'ok' ? <p className="lc-hint">{lecture.message}</p> : <p className="lc-hint">Lecture en cours…</p>
  if (d.items.length === 0) return <p className="lc-hint">{vide}</p>
  return (
    <Zone perime={perime}>
      <ol className="sv-dom-journal">
        {d.items.map((e) => (
          <li key={`${e.id}`} className="sv-dom-journal__ligne">
            <span className="sv-dom-journal__heure sv-dom-mono">{fmtDateHeure(e.at)}</span>
            <Pastille ton={TON_GRAVITE[e.severity] ?? 'gris'}>{LIBELLE_GRAVITE[e.severity] ?? e.severity}</Pastille>
            <span className="sv-dom-journal__texte">
              {texteEvenement(e)}
              {e.uid ? <> · <Link className="sv-dom-lien" href={lienAppareil(e.uid)}>{e.display_name ?? e.uid}</Link></> : null}
            </span>
          </li>
        ))}
      </ol>
    </Zone>
  )
}

/** Lien vers la fiche d'un appareil, avec son nom. */
export function LienAppareil({ uid, nom, zone }: { uid: string; nom: string; zone?: string | null }) {
  const menu = useMenuSonde()
  return <Link className="sv-dom-lien" href={lienAppareil(uid)} {...menu.appareil({ uid, nom, zone_code: zone ?? null })}>{nom}</Link>
}
